<template>
  <div v-if="isAValidAction">
    <Button
      data-testid="insights-button-action"
      class="ma-2"
      color="primary"
      data-tourid="insights-button-action"
      :icon="isIconAppearance ? textContent : completed ? 'check' : ''"
      :plain="isTextAppearance"
      :loading="loading"
      :disabled="loading || completed"
      :outlined="completed"
      :text="!isIconAppearance ? textContent : ''"
      @click="callAction"
    />
    <v-dialog
      v-model="confirmation.show"
      max-width="313"
      data-testid="insights-confirmation-dialog"
    >
      <v-card>
        <v-card-title
          class="font-size-20 font-bold"
          data-testid="insights-confirmation-title"
          >{{ confirmation.title }}</v-card-title
        >

        <v-card-text data-testid="insights-confirmation-content">
          {{ confirmation.content }}
        </v-card-text>

        <v-card-actions class="justify-end">
          <Button
            color="secondary"
            plain
            data-testid="insights-confirmation-cancel"
            :text="confirmation.cancel"
            @click="confirmation.show = false"
          />
          <Button
            color="primary"
            plain
            data-testid="insights-confirmation-confirm"
            :text="confirmation.confirm"
            @click="confirmation.confirm_action"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ChallengeModal
      :show="showChallengeModal"
      @close="showChallengeModal = false"
    />
    <CreateRevModalByType
      v-if="revType"
      :show="showCreateRev"
      :type="revType"
      @close="showCreateRev = false"
      @created="showCreateRev = false"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapState, mapActions } from 'vuex'

import challengePermissions from '@/mixins/challengePermissions.vue'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import ChallengeModal from '@/components/ChallengeModal/ChallengeModal.vue'
import Button from '@/components/UI/Button'
import CreateRevModalByType from '@/components/CreateRevModalByType/CreateRevModalByType.vue'

const VALID_ENTITIES = [
  'improvement_idea',
  'problem_solving',
  'quick_action',
  'challenge',
  'url',
]

const EVENTS_WITHOUT_ENTITY = ['create_challenge']

export default {
  name: 'ActionsButton',

  mixins: [challengePermissions, mixin, trackerMixin],

  props: {
    data: {
      type: Object,
      required: true,
    },
  },

  components: {
    ChallengeModal,
    Button,
    CreateRevModalByType,
  },

  data() {
    return {
      loading: false,
      completed: false,
      showQuickActionModal: false,
      showImprovementIdeaModal: false,
      showProblemSolvingModal: false,
      showChallengeModal: false,
      confirmation: {
        show: false,
        title: '',
        content: '',
        confirm: this.$t('common.button.confirm'),
        cancel: this.$t('common.button.cancel'),
        confirm_action: null,
      },
      actions: {
        create_rev: this.openCreateEntityModal,
        create_challenge: this.openCreateChallengeModal,
        validate_rev: this.openValidationsModal,
        share: this.openShareModal,
        navigate_to: this.navigateToView,
        add_impacts: this.navigateToRevReview,
        share_to_user: this.shareToUser,
        link_challenge: this.linkChallengeDialog,
      },
      revType: null,
      showCreateRev: false,
    }
  },

  computed: {
    ...mapGetters(['accountId', 'organization', 'organizationId']),
    ...mapState({
      activeReport: state => state.report.activeReport,
      activeFilters: state => state.report.activeFilters,
    }),

    actionType() {
      return this.data.type
    },
    textContent() {
      return this.data?.text ?? ''
    },
    entityId() {
      return this.data?.entityId
    },
    entityType() {
      return this.data?.entityType ?? ''
    },
    camelCaseEntityType() {
      return _.camelCase(this.entityType)
    },

    isIconAppearance() {
      return this.data?.appearance === 'icon'
    },
    isTextAppearance() {
      return this.data?.appearance === 'text'
    },
    metadata() {
      return this.data?.metadata ?? {}
    },

    isAValidAction() {
      const validActions = Object.keys(this.actions)
      const hasValidEntity = VALID_ENTITIES.includes(
        _.snakeCase(this.entityType)
      )
      const entityIsNotRequired = EVENTS_WITHOUT_ENTITY.includes(
        this.actionType
      )
      const hasTextProperty = this.textContent.length

      return (
        validActions.includes(this.actionType) &&
        (hasValidEntity || entityIsNotRequired) &&
        hasTextProperty
      )
    },
  },

  methods: {
    ...mapActions({
      showModal: 'modals/showModal',
      updateRev: 'report/updateRev',
    }),

    callAction() {
      this.$track('insights.action', this.$store, {
        action: this.actionType,
        reportType: this.activeReport,
      })
      this.actions[this.actionType]()
    },

    startLoading() {
      this.loading = true
    },

    stopLoading() {
      this.loading = false
    },

    markAsComplete() {
      this.completed = true
    },

    getCreateEntityModalTitle(type) {
      const customName = this.organization?.customNamings[type]?.singular ?? ''
      const revType = _.snakeCase(type)
      const createRevTranslation = 'rev.create.create_new_'

      return customName
        ? this.$t('rev.create.create_new_custom_name', { customName })
        : this.$t(`${createRevTranslation}${revType}`)
    },

    openCreateEntityModal() {
      const revType = this.camelCaseEntityType
      this.revType = revType
      this.showCreateRev = true
    },

    openCreateChallengeModal() {
      if (!this.canCreateChallenges()) return
      this.showChallengeModal = true
    },

    openValidationsModal() {
      try {
        switch (this.entityType) {
          case 'quick_action':
            this.showQuickActionModal = true
            break
          case 'problem_solving':
            this.showProblemSolvingModal = true
            break
          case 'improvement_idea':
            this.showImprovementIdeaModal = true
            break
        }
      } catch (e) {
        this.showError(
          this.$t(
            'error.500.something_bad_happened_we_probably_did_something_wrong'
          )
        )
      }
    },

    openShareModal() {
      const titleType = this.entityType === 'challenge' ? 'challenge' : 'rev'
      const entity =
        this.camelCaseEntityType === 'improvementIdea'
          ? 'rev'
          : this.camelCaseEntityType
      this.showModal({
        type: 'shareModal',
        props: {
          shareType: entity,
          title: this.$t(`common.share.${titleType}`),
          entityId: this.entityId,
          organizationId: this.organizationId,
          sharingAccountId: this.accountId,
          name: this.metadata?.entityName ?? '',
        },
      })
    },

    getShareFilters() {
      const userType = this.metadata?.shareTo ?? ''
      if (userType === 'financial-coach') {
        return { canDoFinancialValidation: true }
      }

      const userSite = this.account.site._id
      return {
        isCoach: true,
        site: userSite,
      }
    },

    async shareToUser() {
      const titleType = this.entityType === 'challenge' ? 'challenge' : 'rev'
      const entity =
        this.camelCaseEntityType === 'improvementIdea'
          ? 'rev'
          : this.camelCaseEntityType
      this.showModal({
        type: 'shareModal',
        props: {
          shareType: entity,
          title: this.$t(`common.share.${titleType}`),
          entityId: this.entityId,
          organizationId: this.organizationId,
          sharingAccountId: this.accountId,
          filters: this.getShareFilters(),
        },
      })
    },

    navigateToView() {
      if (this.entityType === 'url') {
        return this.navigateTo(this.metadata.url)
      }
      if (!this.entityId) {
        return this.navigateTo(`/${this.entityType}`)
      }

      let kebabCaseType = _.kebabCase(this.entityType)
      if (kebabCaseType === 'improvement-idea') kebabCaseType = 'rev'
      return this.navigateTo(
        `/${kebabCaseType}/${this.organizationId}/${this.entityId}`
      )
    },

    navigateToRevReview() {
      const reviewTabIndexBytype = {
        improvementIdea: 4,
        quickAction: 2,
        problemSolving: 2,
      }
      return this.navigateTo({
        name: this.camelCaseEntityType,
        params: {
          organizationId: this.organizationId,
          revId: this.entityId,
          initialTab: reviewTabIndexBytype[this.camelCaseEntityType],
          mode: 'edit',
        },
      })
    },

    linkChallengeDialog() {
      const challengeName = this.metadata?.challengeName ?? ''
      this.confirmation.title = this.$t('insights.link_it_to_a_challenge')
      this.confirmation.content = this.$t(
        'insights.are_you_sure_to_link_the_challenge',
        { challengeName }
      )
      this.confirmation.confirm_action = this.linkRevToChallenge
      this.confirmation.show = true
    },

    linkRevToChallenge() {
      try {
        this.startLoading()
        const challengeId = this.metadata?.challengeId ?? ''
        let kebabCaseType = _.kebabCase(this.entityType)
        // the following is needed given the tech debt
        // is needed to change this in api and urls
        if (kebabCaseType === 'improvement-idea') kebabCaseType = 'rev'
        this.updateRev({
          organizationId: this.organizationId,
          revType: kebabCaseType,
          revId: this.entityId,
          data: {
            challenge: challengeId,
          },
        })
        this.markAsComplete()
      } catch (e) {
        this.showError(
          this.$t(
            'error.500.something_bad_happened_we_probably_did_something_wrong'
          )
        )
      } finally {
        this.stopLoading()
        this.confirmation.show = false
      }
    },
  },
}
</script>
